import { GetMatchesNotifications } from "../services/backend";
import { ensureMatchConversation } from "./ensureMatchConversation";

/** Unwrap axios / API envelopes so match detection works across response shapes. */
function unwrapLikeResponse(likeResponse) {
  if (likeResponse == null) return null;
  if (typeof likeResponse === "string") {
    return { message: likeResponse };
  }
  if (typeof likeResponse !== "object") return null;
  if (likeResponse.data != null && typeof likeResponse.data === "object") {
    return likeResponse.data;
  }
  if (likeResponse.result != null && typeof likeResponse.result === "object") {
    return likeResponse.result;
  }
  return likeResponse;
}

function normalizeMessage(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

function extractMatchPayload(body) {
  if (!body || typeof body !== "object") return null;
  return (
    body.matchWith ??
    body.MatchWith ??
    body.matchedUser ??
    body.MatchedUser ??
    body.match ??
    body.Match ??
    null
  );
}

/** True when AddLike indicates a mutual match (camelCase or PascalCase API). */
export function isLikeMatchResponse(likeResponse) {
  const body = unwrapLikeResponse(likeResponse);
  if (!body) return false;

  const message = normalizeMessage(
    body.message ?? body.Message ?? body.statusMessage ?? body.StatusMessage
  );
  if (message.includes("match")) return true;

  const statusText = normalizeMessage(body.status ?? body.Status);
  if (statusText.includes("match")) return true;

  if (
    body.isMatch === true ||
    body.IsMatch === true ||
    body.matched === true ||
    body.Matched === true ||
    body.isMutual === true ||
    body.IsMutual === true ||
    body.createdMatch === true ||
    body.CreatedMatch === true
  ) {
    return true;
  }

  return Boolean(extractMatchPayload(body));
}

export function getMatchedUserFromResponse(likeResponse, fallbackUser) {
  const body = unwrapLikeResponse(likeResponse);
  if (!body) return fallbackUser || null;

  const raw =
    extractMatchPayload(body) ?? body.user ?? body.User;

  if (!raw) return fallbackUser || null;
  if (typeof raw === "string") return fallbackUser || null;
  if (raw.user && typeof raw.user === "object") {
    return { ...raw, ...raw.user };
  }
  return raw;
}

export function getMatchConversationId(likeResponse) {
  const body = unwrapLikeResponse(likeResponse);
  if (!body) return null;

  const matchPayload = extractMatchPayload(body);

  return (
    body.conversationId ??
    body.ConversationId ??
    matchPayload?.conversationId ??
    matchPayload?.ConversationId ??
    matchPayload?.sendBirdUri ??
    matchPayload?.SendBirdUri ??
    body.sendBirdUri ??
    body.SendBirdUri ??
    null
  );
}

function notifyConversationsChanged(conversationId) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("payinapo:conversations-changed", {
      detail: { conversationId: conversationId || null, reason: "match" },
    })
  );
}

async function enrichPayloadWithConversation(payload, currentUserId) {
  if (!payload?.matchedUser || !currentUserId) return payload;

  let conversationId = payload.conversationId || null;

  if (!conversationId) {
    conversationId = await ensureMatchConversation(
      currentUserId,
      payload.matchedUser
    );
  }

  // Always refresh inboxes for both users after a match (shared conversation).
  notifyConversationsChanged(conversationId);

  if (!conversationId) return payload;
  return { ...payload, conversationId };
}

function findMatchInNotifications(matches, likedUserId) {
  if (!Array.isArray(matches) || !likedUserId) return null;
  return matches.find((entry) => {
    const uid = entry?.user?.id ?? entry?.userId ?? entry?.UserId;
    return uid != null && String(uid) === String(likedUserId);
  });
}

/**
 * Resolve match celebration payload from AddLike response and/or fresh match notifications.
 * @param {object} options.assumeMutualMatch - true when liking someone who already liked you
 */
export async function resolveMatchCelebration(
  likeResponse,
  fallbackUser,
  options = {}
) {
  const { assumeMutualMatch = false, currentUserId = null } = options;
  const likedUserId = fallbackUser?.id ?? fallbackUser?.user?.id ?? null;

  let payload = openMatchCelebrationFromLikeResponse(likeResponse, fallbackUser);
  if (payload) {
    return enrichPayloadWithConversation(payload, currentUserId);
  }

  if (assumeMutualMatch && fallbackUser) {
    return enrichPayloadWithConversation(
      {
        matchedUser: fallbackUser,
        conversationId: getMatchConversationId(likeResponse),
      },
      currentUserId
    );
  }

  if (currentUserId && likedUserId) {
    const delays = [0, 350, 800];
    for (const delayMs of delays) {
      if (delayMs > 0) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
      try {
        const matches = await GetMatchesNotifications(currentUserId);
        const found = findMatchInNotifications(matches, likedUserId);
        if (found) {
          return enrichPayloadWithConversation(
            {
              matchedUser: found.user ?? fallbackUser,
              conversationId:
                found.sendBirdUri ??
                found.SendBirdUri ??
                found.conversationId ??
                found.ConversationId ??
                getMatchConversationId(likeResponse),
            },
            currentUserId
          );
        }
      } catch (e) {
        console.warn("Could not verify match via notifications:", e);
        break;
      }
    }
  }

  return null;
}

export function openMatchCelebrationFromLikeResponse(likeResponse, fallbackUser) {
  if (!isLikeMatchResponse(likeResponse)) {
    return null;
  }
  const matchedUser = getMatchedUserFromResponse(likeResponse, fallbackUser);
  if (!matchedUser) return null;
  return {
    matchedUser,
    conversationId: getMatchConversationId(likeResponse),
  };
}
